import { useCallback, useEffect, useState } from 'react';
import {
  UseKeyboardNavigationProps,
  UseKeyboardNavigationReturn,
} from './index.types';

/**
 * A React hook for managing focus of elements in a list based on keyboard
 * arrows (up and down). Great for a11y and UX.
 *
 * This hook does not deal with the actual imperative `.focus()` part at all.
 * It solely calculates which index in a list should currently be focused.
 *
 * The component using this hook is responsible for:
 * - Managing refs to focusable elements
 * - Calling .focus() when focusedIndex changes
 * - Setting tabIndex appropriately
 */
export function useKeyboardNavigation({
  isExpanded,
  itemCount,
  onSelect,
}: UseKeyboardNavigationProps): UseKeyboardNavigationReturn {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!isExpanded) {
      setFocusedIndex(null);
    }
  }, [isExpanded]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isExpanded || itemCount === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) =>
            prev === null ? 0 : prev === itemCount - 1 ? 0 : prev + 1
          );
          break;

        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) =>
            prev === null
              ? itemCount - 1
              : prev === 0
              ? itemCount - 1
              : prev - 1
          );
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          if (
            focusedIndex !== null &&
            focusedIndex >= 0 &&
            focusedIndex < itemCount
          ) {
            onSelect(focusedIndex);
          }
          break;

        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          break;

        case 'End':
          event.preventDefault();
          setFocusedIndex(itemCount - 1);
          break;

        case 'Escape':
          event.preventDefault();
          setFocusedIndex(null);
          break;

        default:
          break;
      }
    },
    [isExpanded, itemCount, focusedIndex, onSelect]
  );

  return {
    focusedIndex,
    setFocusedIndex,
    handleKeyDown,
  };
}
